import classNames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Markdown from 'markdown-to-jsx';

import { initChatbot, streamChatbot } from '../../api';
import { getSessionMessages, getSessionThreadId, setSessionMessages, setSessionThreadId } from '../../storage';
import { ChatMessage } from '../../types';
import { createUuid } from '../../utils';

import styles from './ChatbotWidget.module.scss';

type ChatbotConfig = {
  title: string;
  description?: string;
  initial_msg?: string;
};

function createMessage(role: 'user' | 'assistant', content: string): ChatMessage {
  return {
    id: createUuid(),
    role,
    content,
    createdAt: Date.now()
  };
}

export default function ChatbotWidget() {
  const [config, setConfig] = useState<ChatbotConfig | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const [messages, setMessages] = useState<ChatMessage[]>(() => getSessionMessages());
  const [draft, setDraft] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [activeAssistantMessageId, setActiveAssistantMessageId] = useState<string | null>(null);

  const threadId = useMemo(() => {
    const existing = getSessionThreadId();
    if (existing) return existing;
    const created = createUuid();
    setSessionThreadId(created);
    return created;
  }, []);

  const abortRef = useRef<AbortController | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const scrollMessagesToBottom = () => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  };

  useEffect(() => {
    setSessionMessages(messages);
  }, [messages]);

  useEffect(() => {
    const controller = new AbortController();

    initChatbot(controller.signal)
      .then(data => {
        setConfig(data);
        setIsReady(true);

        setMessages(prev => {
          if (prev.length > 0) return prev;
          if (data.initial_msg) return [createMessage('assistant', data.initial_msg)];
          return prev;
        });
      })
      .catch(() => {
        // silent: widget should not show if init fails
        setIsReady(false);
      });

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => {
      listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
      inputRef.current?.focus();
    }, 50);
    return () => clearTimeout(t);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [isOpen, messages.length]);

  const canSend = useMemo(() => {
    return isReady && !!config && !isStreaming && draft.trim().length > 0;
  }, [config, draft, isReady, isStreaming]);

  const toggleOpen = () => setIsOpen(v => !v);

  const onSend = async () => {
    const prompt = draft.trim();
    if (!prompt || !config || !isReady || isStreaming) return;

    setDraft('');
    const userMsg = createMessage('user', prompt);
    const assistantMsg = createMessage('assistant', '');

    setMessages(prev => [...prev, userMsg, assistantMsg]);
    setIsOpen(true);
    setIsStreaming(true);
    setActiveAssistantMessageId(assistantMsg.id);

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await streamChatbot({
        prompt,
        thread_id: threadId,
        signal: controller.signal,
        onDone: finalText => {
          setMessages(prev => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (!last || last.id !== assistantMsg.id) return prev;
            next[next.length - 1] = { ...last, content: finalText };
            return next;
          });
          scrollMessagesToBottom();
        }
      });
    } catch {
      if (controller.signal.aborted) {
        return;
      }
      setMessages(prev => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (!last || last.id !== assistantMsg.id) return prev;
        next[next.length - 1] = {
          ...last,
          content: "Kechirasiz, hozir javob bera olmadim. Keyinroq qayta urinib ko'ring."
        };
        return next;
      });
    } finally {
      setIsStreaming(false);
      setActiveAssistantMessageId(null);
      abortRef.current = null;
      scrollMessagesToBottom();
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  if (!isReady || !config) return null;

  return (
    <div className={styles.root}>
      {isOpen && (
        <div className={styles.panel} role="dialog" aria-label={config.title}>
          <div className={styles.header}>
            <div className={styles.brandDot} aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 4a8 8 0 0 0-8 8c0 2.02.75 3.86 1.99 5.26L5 20l2.91-.92A7.96 7.96 0 0 0 12 20a8 8 0 0 0 0-16Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className={styles.headerText}>
              <p className={styles.title}>{config.title}</p>
              {!!config.description && <p className={styles.desc}>{config.description}</p>}
            </div>
            <button className={styles.close} onClick={() => setIsOpen(false)} aria-label="Close">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          <div className={styles.messages} ref={listRef}>
            {messages.map(m => (
              <div
                key={m.id}
                className={classNames(styles.row, m.role === 'user' ? styles.rowUser : styles.rowAssistant)}
              >
                <div
                  className={classNames(
                    styles.bubble,
                    m.role === 'user' ? styles.bubbleUser : styles.bubbleAssistant
                  )}
                >
                  {m.role === 'assistant' &&
                  isStreaming &&
                  activeAssistantMessageId === m.id &&
                  !m.content ? (
                    <span className={styles.thinking} aria-label="Loading">
                      <span className={styles.thinkingDots} aria-hidden="true">
                        <span />
                        <span />
                        <span />
                      </span>
                    </span>
                  ) : m.content ? (
                    <>
                      <Markdown>{m.content}</Markdown>
                    </>
                  ) : null}
                </div>
              </div>
            ))}
          </div>

          <div className={styles.composer}>
            <div className={styles.inputWrap}>
              <textarea
                ref={inputRef}
                className={styles.textarea}
                placeholder="Savolingizni yozing…"
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                disabled={isStreaming}
              />
              <button
                className={classNames(styles.send, (!canSend || isStreaming) && styles.sendDisabled)}
                onClick={onSend}
                disabled={!canSend || isStreaming}
                aria-label="Send"
              >
                <span className={styles.sendIcon} aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M10 14 21 3M10 14l3.5 7a.75.75 0 0 0 1.4-.11L21 3 3.11 9.1a.75.75 0 0 0-.11 1.4l7 3.5Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      <button
        className={classNames(styles.fab, isOpen && styles.fabOpen)}
        onClick={toggleOpen}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
        aria-expanded={isOpen}
      >
        {isOpen ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M9 4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1h1.2A2.8 2.8 0 0 1 20 7.8V15a4 4 0 0 1-4 4h-1.6l-2.4 2-2.4-2H8a4 4 0 0 1-4-4V7.8A2.8 2.8 0 0 1 6.8 5H8V4Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <path d="M9 11h.01M15 11h.01" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          </svg>
        )}
      </button>
    </div>
  );
}

